import { Box, Grid, Link, TextField, Typography } from '@mui/material'
import { AppleButton } from '~/components/common/AppleButton'
import { GoogleButton } from '~/components/common/GoogleButton'
import { MarketingButton } from '~/components/common/MarketingButton'
import { SigninSignupContent } from '~/components/common/SigninSignupContent'
import { TermsCheckbox } from '~/components/common/TermsCheckbox'
import { useRouter } from 'next/router'
import { snackActions } from '~/lib/snackbarHelper'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { isMarketingApiError } from '~/lib/types/error/MarketingApiError'
import { EMAIL_FORMAT_REGEXP } from '~/lib/constants'
import { eventPush } from '~/lib/gtmHelper'
import NextLink from 'next/link'
import { pagesPath } from '~/lib/$path'
import { preSignUp } from '~/api/UserAdvRepository'
import OpenInNew from '@mui/icons-material/OpenInNew'
import { auth } from '~/lib/firebase/auth'
import { EXTERNAL_LINKS } from '~/lib/externalLinks'

const itemWrapperStyle = {
  textAlign: 'center',
  marginTop: { xs: '1rem', sm: 0 },
}

const indentStyle = {
  marginLeft: '1rem',
  marginBottom: '0.2rem !important',
  '&::before': {
    content: "'※'",
    marginLeft: '-0.5rem',
  },
}

type FieldTypes = { email: string; term: boolean }

export const SignUpPage = () => {
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = useForm<FieldTypes>()
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(true)

  const router = useRouter()
  const [beforeSent, setBeforeSent] = useState<string>()

  const onSubmitFunc = async (data: FieldTypes) => {
    // 送信中は処理しない
    if (isSubmitting) return
    // 直前に送信されたアドレスと同じ場合は処理しない
    if (beforeSent === data.email) return

    const email: string = data.email
    if (data.email !== '') {
      setBeforeSent(data.email)
      await preSignUp(email)
        .then(() => {
          snackActions.info('仮登録メールを送信しました メールを確認してください')
          eventPush('send_sign_up_link', { method: 'email' })
        })
        .catch((e) => {
          if (isMarketingApiError(e) && e.response.status === 409) {
            router.push(pagesPath.signin.$url())
            snackActions.warning('すでにユーザー登録されています．サインインページへ遷移しました', {
              preventDuplicate: true,
            })
          } else {
            snackActions.error('エラーが発生しました しばらくしてからもう一度お試しください')
            // ネットワークエラーの可能性を考慮し、再送できるように、beforeSentを初期化する
            setBeforeSent(undefined)
          }
        })
    }
  }

  const handleGoogleSignIn = () => auth.signInWithGoogle().catch(() => snackActions.error('Google認証が失敗しました'))

  const handleAppleSignIn = () => auth.signInWithApple().catch(() => snackActions.error('Apple認証が失敗しました'))

  return (
    <SigninSignupContent>
      <form>
        <Grid
          container
          direction="column"
          justifyContent="space-around"
          alignItems="stretch"
          gap={2}
          sx={{
            marginTop: { xs: '3rem', sm: 0 },
            minHeight: '100%',
          }}
        >
          <Box sx={{ height: { xs: 30, md: 42 } }} />
          <Grid sx={{ ...itemWrapperStyle }}>
            <Typography variant="headingM">無料会員登録</Typography>
          </Grid>

          <Grid sx={{ ...itemWrapperStyle }}>
            <Typography variant="h6" fontSize={17}>
              メールアドレスで登録
            </Typography>
          </Grid>
          <Box px={8} sx={{ px: { xs: 4, sm: 8, md: 2, lg: 8 } }}>
            <Grid container justifyContent="flex-start">
              <Box>
                <Typography display="block" variant="caption" sx={{ ...indentStyle }}>
                  複数人でアカウントを管理する場合はメールアドレスで登録をご選択ください。
                </Typography>
                <Typography display="block" variant="caption" sx={{ ...indentStyle }}>
                  複数人でアカウントを管理する場合、会社メールアドレス等の、共有可能なメールアドレスで登録ください。
                </Typography>
                <Typography display="block" variant="caption" sx={{ ...indentStyle }}>
                  個人でアカウント管理する方もご利用いただけます。
                </Typography>
                <Typography display="block" variant="caption" sx={{ ...indentStyle }} color="error">
                  会員登録時のメールアドレスは変更できません。
                </Typography>
              </Box>
            </Grid>
          </Box>
          <Grid
            mt={1}
            px={2}
            container
            direction="row"
            justifyContent="center"
            alignItems="baseline"
            sx={{ ...itemWrapperStyle }}
          >
            <TermsCheckbox
              control={control}
              onChange={(checked: boolean) => setButtonDisabled(!checked)}
              defaultValue={false}
            />
          </Grid>
          <Grid sx={{ ...itemWrapperStyle }}>
            <Controller
              name="email"
              defaultValue=""
              rules={{
                required: 'emailは必須です',
                pattern: {
                  value: EMAIL_FORMAT_REGEXP,
                  message: 'user@example.comの形式です',
                },
              }}
              control={control}
              render={({ field }) => (
                <TextField
                  inputProps={{
                    'aria-label': 'email',
                  }}
                  type="email"
                  variant="outlined"
                  error={!!errors.email}
                  label={<Typography variant="body2">メールアドレス</Typography>}
                  helperText={errors.email?.message}
                  sx={{ width: '60%' }}
                  {...field}
                />
              )}
            />
          </Grid>
          <Grid sx={{ ...itemWrapperStyle }}>
            <MarketingButton
              data-testid="signup-button"
              type="submit"
              variant="contained"
              disabled={buttonDisabled}
              loading={isSubmitting}
              onClick={handleSubmit(onSubmitFunc)}
            >
              メールアドレスで登録
            </MarketingButton>
          </Grid>
          {/* SNSログイン */}
          <Box width="60%" alignItems="center" alignSelf="center" mt={1.5}>
            <Box mx="auto">
              <Grid sx={{ ...itemWrapperStyle }}>
                <GoogleButton
                  name="google-signup"
                  aria-label="Googleで登録"
                  value="Googleで登録"
                  onClick={handleGoogleSignIn}
                  disabled={buttonDisabled}
                  fullWidth
                />
              </Grid>
            </Box>
            <Box sx={{ height: { xs: 0, sm: 10 } }} />
            <Box mx="auto">
              <Grid sx={{ ...itemWrapperStyle }}>
                <AppleButton
                  name="apple-signup"
                  aria-label="Appleで登録"
                  value="Appleで登録"
                  onClick={handleAppleSignIn}
                  disabled={buttonDisabled}
                  fullWidth
                />
              </Grid>
            </Box>
          </Box>
          <Box pt={2}>
            <Grid sx={{ ...itemWrapperStyle, px: 1 }}>
              <NextLink href={pagesPath.signin.$url()} passHref legacyBehavior>
                <Link underline="hover">
                  <Typography color="textPrimary" style={{ textDecoration: 'underline' }}>
                    アカウント登録をお済みの方はこちら
                  </Typography>
                </Link>
              </NextLink>
            </Grid>
          </Box>
          <Box pb={7}>
            <Grid sx={{ ...itemWrapperStyle, px: 1 }}>
              <NextLink href={EXTERNAL_LINKS.SERVICE_REFUSE_POSTING} passHref legacyBehavior>
                <Link underline="hover" target="_blank" rel="noopener noreferrer">
                  <Typography
                    color="textPrimary"
                    variant="caption"
                    fontSize="small"
                    style={{
                      textDecoration: 'underline',
                    }}
                  >
                    掲載をお断りしているサービスについて
                    <OpenInNew style={{ fontSize: 'small' }} />
                  </Typography>
                </Link>
              </NextLink>
            </Grid>
          </Box>
        </Grid>
      </form>
    </SigninSignupContent>
  )
}
